import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Building2, Calendar, Briefcase, AlertCircle } from "lucide-react";
import { calcularDiasRestantes, formatDateBR } from "@/utils/dateUtils";
import { projectArea } from "@/utils/project-area";

interface Projeto {
  id: string;
  area_atuacao: string;
  area_canonica?: string | null;
  data_inicio: string;
  data_conclusao: string | null;
  agencia?: {
    nome: string;
  };
}

interface FreelancerProjetosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancerNome: string;
  projetos: Projeto[];
}

export function FreelancerProjetosModal({
  open,
  onOpenChange,
  freelancerNome,
  projetos,
}: FreelancerProjetosModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            Projetos em Andamento - {freelancerNome}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-3">
            {projetos.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-lg font-medium">Nenhum projeto ativo</p>
              </div>
            ) : (
              projetos.map((projeto, index) => {
                const diasRestantes = calcularDiasRestantes(projeto.data_conclusao);
                const proximoDeConcluir = diasRestantes !== null && diasRestantes >= 0 && diasRestantes < 10;
                const atrasado = diasRestantes !== null && diasRestantes < 0;

                return (
                  <div
                    key={projeto.id}
                    className={`p-4 rounded-lg border ${
                      proximoDeConcluir 
                        ? 'border-orange-200 bg-orange-50/50' 
                        : atrasado
                          ? 'border-red-200 bg-red-50/50'
                          : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                          Projeto {index + 1}
                        </Badge>
                        {proximoDeConcluir && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Termina em {diasRestantes} dias
                          </Badge>
                        )}
                        {atrasado && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Atrasado {Math.abs(diasRestantes!)} dias
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Área de Atuação */}
                    <div className="flex items-start gap-2 mb-2">
                      <Briefcase className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">
                          {projectArea(projeto)}
                        </p>
                      </div>
                    </div>

                    {/* Agência */}
                    {projeto.agencia?.nome && (
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 shrink-0 text-blue-500" />
                        <span className="text-sm text-slate-600">
                          {projeto.agencia.nome}
                        </span>
                      </div>
                    )}

                    {/* Datas */}
                    <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-green-500" />
                        <div className="text-xs">
                          <span className="text-slate-500">Início: </span>
                          <span className="text-slate-700 font-medium">
                            {formatDateBR(projeto.data_inicio)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-red-500" />
                        <div className="text-xs">
                          <span className="text-slate-500">Término: </span>
                          <span className="text-slate-700 font-medium">
                            {projeto.data_conclusao 
                              ? formatDateBR(projeto.data_conclusao)
                              : "Indefinido"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

